import React, { useEffect, useState } from 'react';
import { Field, formValueSelector, reduxForm, change } from 'redux-form';
import { Col, Row, Card, CardBody } from 'reactstrap';
import IconButton from '@mui/material/IconButton';
import InfoIcon from '@mui/icons-material/Info';
import ZonePriceListModal from './ZonePriceListModal';
import PndRestApi from 'src/utils/pnd/PndRestApi';
import { showNotification } from 'src/shared/components/Notification';
import { connect, useSelector } from 'react-redux';
import { isNumber } from 'src/utils/validationUtils';
import { ERROR_MESSAGES } from 'src/utils/pnd/PndConstants';
import renderField from 'src/shared/components/form/renderField';
import renderSelectField from 'src/shared/components/form/Select';
import ReceiptIcon from '@mui/icons-material/Receipt';
import PriceCheckIcon from '@mui/icons-material/PriceCheck';
import PaymentIcon from '@mui/icons-material/Payment';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import DescriptionIcon from '@mui/icons-material/Description';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';

const required = value => (value !== undefined && value !== null && value !== '') ? undefined : 'Required';

const OrderBillingAddorUpdate = ({ dispatch, orderSalesAmount, orderAmount, deliveryRate, deliveryMode, isInfo }) => {

    useEffect(() => {
        getAllDeliveryAreaRateDetails();
    }, [])

    const [isZonePriceModalOpen, setZonePriceModalOpen] = useState(false);
    const [isDeliveryAreaIds, setDeliveryAreaIds] = useState([]);
    const [isDeliveryRate, setDeliveryRate] = useState([]);
    const [isDeliveryCompSelected, setDeliveryCompSelected] = useState(null);
    const orderPreferences = useSelector(state => state.orderPreferences);

    const handleInfoButtonClick = () => {
        setZonePriceModalOpen(true);
    };

    /* -------- Auto Calculate Balance (ADD + EDIT) -------- */
    useEffect(() => {
        const sales = Number(orderSalesAmount) || 0;
        const paid = Number(orderAmount) || 0;
        const rate = Number(deliveryRate) || 0;

        let balance = 0;

        // Customer pays delivery
        if (deliveryMode?.value === 1) {
            balance = sales - paid + rate;
        } else {
            balance = sales - paid;
        }
        balance = Math.max(balance, 0);

        dispatch(change('orderForm', 'orderTotalAmount', balance.toFixed(2)));
    }, [orderSalesAmount, orderAmount, deliveryRate, deliveryMode, dispatch]);

    const calculateDeliveryCharges = (event) => {
        dispatch(change('orderForm', 'orderAmount', ""));
        if (isDeliveryCompSelected == null) {
            showNotification(
                "theme-light",
                "ltr",
                `SuccessCode: 500`,
                `Message:Please select Delivery Zone.`,
            );
        }
    };

    const getDeliveryAreaRateById = async (rateId) => {
        try {
            dispatch(change('orderForm', 'orderTotalAmount', ""));
            dispatch(change('orderForm', 'orderAmount', ""));

            const response = await PndRestApi.GetDeliveryAreaRateById(rateId);
            if (response.status === 200 && response.data.status === 200) {
                dispatch(change('orderForm', 'orderProcessingAreaRate', response.data?.data?.rate));
            } else {
                showNotification(
                    "theme-light",
                    "ltr",
                    `SuccessCode: ${response?.data?.status}`,
                    `Message: Delivery Rate is missing. Please configure first`,
                );
            }
        } catch (error) {
            console.error("Error in getDeliveryAreaRateById", error);
            showNotification(
                "theme-light", "ltr", "SuccessCode: 500", "Message:Something went wrong. Please try again later.",
            );
        }
    };

    const getAllDeliveryAreaRateDetails = async () => {
        try {
            let deliveryAreaIds = [];
            const response = await PndRestApi.GetAllDeliveryAreaRateDetails();
            if (response.status === 200 && response.data.status === 200) {
                setDeliveryRate(response.data.data);
                response.data.data.map(item => {
                    deliveryAreaIds.push({ "label": item.areaName, "value": item.deliveryAreaId });
                    return null;
                })
                setDeliveryAreaIds(deliveryAreaIds);
            } else if (response.status === 200 && response.data.status === 204) {
                showNotification("theme-light", "ltr", `SuccessCode: ${response.data.status}`, `Message:Please define Delivery Area first`);
            } else {
                showNotification("theme-light", "ltr", `SuccessCode: ${response.data.status}`, `Message:${response.data.message}`);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
            showNotification("theme-light", "ltr", "SuccessCode: 500", ERROR_MESSAGES.tryCatchError);
        }
    };

    const getDeliverRate = (event) => {
        if (event && event?.value) {
            setDeliveryCompSelected(event.value);
            getDeliveryAreaRateById(event.value);
        }
    };

    return (
        <Card className="border-0 shadow-sm mb-3">
            <CardBody className="p-3">
                <div className="d-flex align-items-center mb-3 text-primary border-bottom pb-2">
                    <ReceiptIcon style={{ fontSize: '1.2rem' }} className="me-2" />
                    <h6 className="m-0 fw-bold small text-uppercase">Billing Information</h6>
                </div>
                <Row className="g-2 mb-2">
                    <Col lg={3} md={6}>
                        <div className="mb-1">
                            <label className="form-label fw-bold text-muted small mb-0" style={{ fontSize: '0.75rem' }}>Invoice / DLV Note No. <span className='text-danger'>*</span></label>
                            <Field name="invoiceNo" component={renderField} type="text" placeholder="Invoice No." validate={[required, isNumber]} icon={ReceiptIcon} />
                        </div>
                    </Col>
                    <Col lg={3} md={6}>
                        <div className="mb-1">
                            <label className="form-label fw-bold text-muted small mb-0" style={{ fontSize: '0.75rem' }}>Ecommerce No.</label>
                            <Field name="salesOrderNo" component={renderField} type="text" placeholder="Ecommerce No." icon={ShoppingBagIcon} />
                        </div>
                    </Col>
                    <Col lg={3} md={6}>
                        <div className="mb-1">
                            <label className="form-label fw-bold text-muted small mb-0" style={{ fontSize: '0.75rem' }}>Delivery Zone <span className='text-danger'>*</span></label>
                            <div className="d-flex align-items-center">
                                <div className="flex-grow-1">
                                    <Field name="orderZoneId" component={renderSelectField} options={isDeliveryAreaIds} placeholder="Select Zone" onChange={getDeliverRate} validate={[required]} />
                                </div>
                                <IconButton color="success" size="small" className="ms-1" onClick={handleInfoButtonClick}>
                                    <InfoIcon style={{ fontSize: '1.2rem' }} />
                                </IconButton>
                            </div>
                        </div>
                    </Col>
                    <Col lg={3} md={6}>
                        <div className="mb-1">
                            <label className="form-label fw-bold text-muted small mb-0" style={{ fontSize: '0.75rem' }}>Delivery Charge</label>
                            <Field name="orderProcessingAreaRate" component={renderField} type="text" placeholder="0.00" disabled={true} icon={LocalShippingIcon} />
                        </div>
                    </Col>
                </Row>
                <Row className="g-2 mb-2">
                    <Col lg={3} md={6}>
                        <div className="mb-1">
                            <label className="form-label fw-bold text-muted small mb-0" style={{ fontSize: '0.75rem' }}>Payment Method <span className='text-danger'>*</span></label>
                            <Field name="paymentMethodInfo" component={renderSelectField} validate={[required]} options={[
                                { value: 1, label: "Cash Prepaid" },
                                { value: 2, label: "Card Payment" },
                                { value: 3, label: "Cash on Delivery" },
                                { value: 4, label: "Card Payment on Delivery" },
                            ]} placeholder="Select Method" />
                        </div>
                    </Col>
                    <Col lg={3} md={6}>
                        <div className="mb-1">
                            <label className="form-label fw-bold text-muted small mb-0" style={{ fontSize: '0.75rem' }}>Charge Collect From <span className='text-danger'>*</span></label>
                            <Field name="deliveryChargeBearerInfo" component={renderSelectField} validate={[required]} options={[
                                { value: 1, label: "Customer" },
                                { value: 2, label: "Merchant" },
                                { value: 3, label: "Hub" },
                                { value: 4, label: "Free Delivery" },
                            ]} placeholder="Select Bearer" onChange={calculateDeliveryCharges} />
                        </div>
                    </Col>
                    <Col lg={2} md={4}>
                        <div className="mb-1">
                            <label className="form-label fw-bold text-muted small mb-0" style={{ fontSize: '0.75rem' }}>Invoice Amount <span className='text-danger'>*</span></label>
                            <Field name="orderSalesAmount" component={renderField} type="text" placeholder="0.00" validate={[required, isNumber]} icon={PriceCheckIcon} />
                        </div>
                    </Col>
                    <Col lg={2} md={4}>
                        <div className="mb-1">
                            <label className="form-label fw-bold text-muted small mb-0" style={{ fontSize: '0.75rem' }}>Amount Paid <span className='text-danger'>*</span></label>
                            <Field name="orderAmount" component={renderField} type="text" placeholder="0.00" validate={[required, isNumber]} icon={PaymentIcon} />
                        </div>
                    </Col>
                    <Col lg={2} md={4}>
                        <div className="mb-1 text-center">
                            <label className="form-label fw-bold text-primary small mb-0" style={{ fontSize: '0.75rem' }}>BALANCE TO COLLECT</label>
                            <Field name="orderTotalAmount" component={renderField} type="text" placeholder="0.00" disabled={true} icon={AccountBalanceWalletIcon} className="text-center font-weight-bold" />
                        </div>
                    </Col>
                </Row>
                <Row className="g-2">
                    <Col lg={12}>
                        <div className="mb-1">
                            <label className="form-label fw-bold text-muted small mb-0" style={{ fontSize: '0.75rem' }}>Shipping Instructions</label>
                            <Field name="orderInstruction" component={renderField} type="textarea" placeholder="Enter instructions..." icon={DescriptionIcon} />
                        </div>
                    </Col>
                </Row>
                {isZonePriceModalOpen &&
                    <ZonePriceListModal open={isZonePriceModalOpen} setOpen={setZonePriceModalOpen} isDeliveryRate={isDeliveryRate} isTitle={"Zone Wise Price Details"} />
                }
            </CardBody>
        </Card>
    );
};

const selector = formValueSelector('orderForm');

const mapStateToProps = (state, ownProps) => {
    const orderSalesAmount = selector(state, 'orderSalesAmount');
    const orderAmount = selector(state, 'orderAmount');
    const deliveryRate = selector(state, 'orderProcessingAreaRate');
    const deliveryMode = selector(state, 'deliveryChargeBearerInfo');

    let initialValues = {};
    if (ownProps?.isInfo) {
        const info = ownProps.isInfo;
        initialValues = {
            name: info?.name,
            invoiceNo: info?.invoiceNo ? info?.invoiceNo : "N/A",
            orderInstruction: info?.orderInstruction,
            orderProcessingAreaRate: info?.orderProcessingAreaRate,
            orderSalesAmount: info?.orderSalesAmount,
            orderTotalAmount: info?.orderTotalAmount,
            orderAmount: info?.orderAmount,
            salesOrderNo: info?.salesOrderNo,
            remarks: info?.remarks,
            paymentMethodInfo: info?.paymentMethodInfo ? { value: info.paymentMethodInfo.paymentMethodId, label: info.paymentMethodInfo.description } : null,
            deliveryChargeBearerInfo: info?.deliveryChargeBearerInfo ? { value: info.deliveryChargeId, label: info.deliveryChargeBearerName } : null, // Fixed: deliveryChargeId
            pickUpPoints: info?.pickUpPointPointId ? { value: info.pickUpPointPointId, label: info.collectionPointName } : null
        };
    }

    return { orderSalesAmount, orderAmount, deliveryRate, deliveryMode, initialValues };
};

export default connect(mapStateToProps)(
    reduxForm({
        form: 'orderForm',
        enableReinitialize: false,
        destroyOnUnmount: false,
        forceUnregisterOnUnmount: false,
    })(OrderBillingAddorUpdate)
);